import React, { useState } from 'react';
import styled from '@emotion/styled';
import Modal from 'react-modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import TurnstileWidget from './TurnstileWidget';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid rgba(255, 51, 51, 0.3)',
    borderRadius: '15px',
    padding: '2rem',
    maxWidth: '500px',
    width: '90%',
    backdropFilter: 'blur(10px)',
    zIndex: 1000,
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(5px)',
    zIndex: 999,
  },
};

const successModalStyles = {
  content: {
    ...modalStyles.content,
    maxWidth: '400px',
    textAlign: 'center',
    background: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid rgba(255, 51, 51, 0.3)',
  },
  overlay: modalStyles.overlay,
};

const FormContainer = styled.div`
  color: white;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  span {
    color: #ff3333;
    font-size: 2.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled(Field)`
  width: 100%;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 51, 51, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ErrorMessage = styled.div`
  color: #ff3333;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: rgba(80, 0, 0, 0.8);
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(120, 0, 0, 0.9);
    border-color: rgba(255, 51, 51, 0.5);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: white;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: rgba(255, 51, 51, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #ff3333;
  border: 2px solid rgba(255, 51, 51, 0.3);
  animation: scaleIn 0.3s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const SuccessTitle = styled.h2`
  font-size: 1.8rem;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 0 0 20px rgba(255, 51, 51, 0.3);
`;

const SuccessMessage = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  line-height: 1.5;
  margin-bottom: 2rem;
`;

const SuccessButton = styled.button`
  padding: 0.8rem 2rem;
  background: rgba(255, 51, 51, 0.8);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 51, 51, 1);
    transform: translateY(-1px);
  }
`;

const disposableEmailDomains = [
  'tempmail.com', 'throwawaymail.com', 'mailinator.com', 
  'temp-mail.org', 'guerrillamail.com', '10minutemail.com'
];

const FormattedPhoneInput = ({ field, form, ...props }) => {
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = parsePhoneNumberFromString(value, 'US');
    return phoneNumber ? phoneNumber.format('NATIONAL') : value;
  };

  const handleChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value.length <= 10) {
      const formatted = formatPhoneNumber(value);
      form.setFieldValue(field.name, formatted);
    }
  };

  return (
    <Input
      {...field}
      {...props}
      onChange={handleChange}
      type="tel"
      placeholder="(555) 555-5555"
    />
  );
};

const FormattedNameInput = ({ field, form, ...props }) => {
  const handleChange = (e) => {
    const value = e.target.value;
    // Only allow letters, spaces, hyphens, and apostrophes
    if (/^[a-zA-Z\s'-]*$/.test(value)) {
      // Capitalize first letter of each word
      const formatted = value.replace(/\b\w/g, char => char.toUpperCase());
      form.setFieldValue(field.name, formatted);
    }
  };

  return (
    <Input
      {...field}
      {...props}
      onChange={handleChange}
      type="text"
      maxLength={50}
    />
  );
};

const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required('Full name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'Name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format')
    .max(254, 'Email must be less than 254 characters')
    .test('no-disposable', 'Please use a valid business email', value => {
      if (!value) return true;
      const domain = value.split('@')[1];
      return !disposableEmailDomains.includes(domain?.toLowerCase());
    })
    .test('email-typo', 'Did you mean to type .com?', value => {
      if (!value) return true;
      if (value.endsWith('.con')) return false;
      return true;
    }),
  phone: Yup.string()
    .required('Phone number is required')
    .test('phone', 'Please enter a valid US phone number', value => {
      if (!value) return true;
      const phoneNumber = parsePhoneNumberFromString(value, 'US');
      return phoneNumber ? phoneNumber.isValid() : false;
    }),
});

Modal.setAppElement('#root');

function PricingForm({ isOpen, onClose, planName, planPrice }) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const turnstileRef = React.useRef(null);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const token = turnstileToken;
      if (!token) {
        setSubmitError('Please complete the Cloudflare Turnstile verification');
        setSubmitting(false);
        return;
      }

      // Format phone number for consistency
      const phoneNumber = parsePhoneNumberFromString(values.phone, 'US');
      const formattedPhone = phoneNumber ? phoneNumber.format('NATIONAL') : values.phone;

      const res = await fetch('/api/pricing', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: values.fullName,
          email: values.email,
          phone: formattedPhone,
          planName,
          planPrice,
          turnstileToken: token
        })
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setSubmitError(data?.error || 'There was an error sending your request. Please try again.');
        setTurnstileToken(null);
        turnstileRef.current?.reset?.();
        setSubmitting(false);
        return;
      }
      
      resetForm();
      setSubmitError('');
      setShowSuccess(true);
      setTurnstileToken(null);
      turnstileRef.current?.reset?.();
    } catch (error) {
      setSubmitError('There was an error sending your request. Please try again.');
    }
    setSubmitting(false);
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !showSuccess}
        onRequestClose={onClose}
        style={modalStyles}
        contentLabel="Get Started Form"
      >
        <FormContainer>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          <Title>
            Get Started with<br />
            <span>{planName}</span>
          </Title>

          {submitError && (
            <ErrorMessage style={{ marginBottom: '1rem', textAlign: 'center' }}>
              {submitError}
            </ErrorMessage>
          )}
          
          <Formik
            initialValues={{
              fullName: '',
              email: '',
              phone: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <FormGroup>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Field
                    name="fullName"
                    component={FormattedNameInput}
                    placeholder="John Smith"
                  />
                  {errors.fullName && touched.fullName && (
                    <ErrorMessage>{errors.fullName}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">Email Address</Label>
                  <Field
                    name="email"
                    as={Input}
                    type="email"
                    placeholder="john@company.com"
                  />
                  {errors.email && touched.email && (
                    <ErrorMessage>{errors.email}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Field
                    name="phone"
                    component={FormattedPhoneInput}
                  />
                  {errors.phone && touched.phone && (
                    <ErrorMessage>{errors.phone}</ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup style={{ display: 'flex', justifyContent: 'center' }}>
                  <TurnstileWidget
                    ref={turnstileRef}
                    siteKey={turnstileKey}
                    theme="dark"
                    onToken={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </FormGroup>
                
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Submit Request'}
                </SubmitButton>
              </Form>
            )}
          </Formik>
        </FormContainer>
      </Modal>

      <Modal
        isOpen={showSuccess}
        onRequestClose={handleSuccessClose}
        style={successModalStyles}
        contentLabel="Success Message"
      >
        <SuccessIcon>✓</SuccessIcon>
        <SuccessTitle>Thank You!</SuccessTitle>
        <SuccessMessage>
          Your request for the {planName} plan has been received. Our team will contact you soon to complete your subscription.
        </SuccessMessage>
        <SuccessButton onClick={handleSuccessClose}>
          Close
        </SuccessButton>
      </Modal>
    </>
  );
}

export default PricingForm;
